import { Component, OnInit, HostListener, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import {
  Subject,
  debounceTime,
  distinctUntilChanged,
  takeUntil,
  Observable,
  combineLatest,
  of,
  switchMap,
  throwError,
} from 'rxjs';
import { map } from 'rxjs/operators';
import { ButtonModule } from 'primeng/button';
import { UserProfile, UserRole } from '../../../shared/models/auth.model';
import {
  RoutingSlip,
  RoutingSlipFilters,
  ApprovalStatus,
} from '../../../shared/models/routing-slip.model';
import { formatDateDDMMYYYY } from '../../../shared/utils/date.util';
import { RoutingSlipService } from '../../../shared/services/routing-slip.service';
import { DocusignService } from '../../../shared/services/docusign.service';
import { DocusignModule } from '../../../shared/models/docusign.model';
import {
  FilterTab,
  SortOption,
  PaginationInfo,
} from '../../../shared/models/letters.model';
import { TableColumn } from '../../../shared/models';
import { FilterTabsComponent } from '../../../shared/components/ui/filter-tabs/filter-tabs.component';
import { SearchBarComponent } from '../../../shared/components/ui/search-bar/search-bar.component';
import { SortDropdownComponent } from '../../../shared/components/ui/sort-dropdown/sort-dropdown.component';
import { TableComponent } from '../../../shared/components/ui/table/table.component';
import { PaginationComponent } from '../../../shared/components/ui/pagination/pagination.component';
import {
  ModalComponent,
  ModalType,
} from '../../../shared/components/ui/modal/modal.component';
import { AuthService } from '../../../core/services/auth.service';
import { CardListComponent } from '../../../shared/components/ui/card-list/card-list.component';
import { AuditModalComponent } from '../../../shared/components/ui/audit-modal/audit-modal.component';
import { DocumentViewerModalComponent } from '../../../shared/components/ui/document-viewer-modal/document-viewer-modal.component';
import { DocumentService } from '../../../shared/services/document.service';
import { DocumentAttachment } from '../../../shared/models/routing-slip.model';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { AuditsData } from '../../../shared/models';
import { LoaderComponent } from '../../../shared/components/ui/loader/loader.component';

@Component({
  selector: 'app-routing-slip-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ButtonModule,
    FilterTabsComponent,
    SearchBarComponent,
    SortDropdownComponent,
    TableComponent,
    PaginationComponent,
    ModalComponent,
    CardListComponent,
    AuditModalComponent,
    DocumentViewerModalComponent,
    LoaderComponent,
    ToastModule,
  ],
  providers: [RoutingSlipService, MessageService, DocumentService],
  templateUrl: './routing-slip-list.component.html',
  styleUrl: './routing-slip-list.component.scss',
})
export class RoutingSlipListComponent implements OnInit, OnDestroy {
  // Mobile detection
  isMobile: boolean = false;

  // User profile data - will be set dynamically from authenticated user
  userProfile: UserProfile | null = null;

  // Data properties
  routingSlips: RoutingSlip[] = [];
  loading: boolean = false;
  pageLoading: boolean = true; // Full page loading state

  // Permission-based button visibility
  routingSlipPermissions: Map<string, number> = new Map(); // Map routing slip ID to permission level

  // Pagination
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
    startItem: 1,
    endItem: 10,
  };

  // Table columns
  columns: TableColumn[] = [];

  // Filter properties
  selectedFilter: string = 'all';
  searchTerm: string = '';
  selectedSortOption: string = 'newest';

  // Debounce subject for search
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  // Modal properties
  showModal: boolean = false;
  modalType: ModalType = 'comment';
  modalTitle: string = '';

  // Audit modal properties
  auditData: AuditsData[] = [];
  selectedRow: any = null;
  auditDialogVisible: boolean = false;
  auditLoading: boolean = false;

  // Document viewer modal properties
  showDocumentViewer: boolean = false;
  currentDocument: DocumentAttachment | null = null;
  documentLoading: boolean = false; // Document loading state
  currentDocumentName: string = ''; // Document name for display

  // Delete/Archive modal properties
  showDeleteModal: boolean = false;
  selectedRoutingSlipForDelete: RoutingSlip | null = null;
  deleteLoading: boolean = false;

  // Filter options
  filterOptions: FilterTab[] = [
    { id: 'all', label: 'All' },
    { id: 'in-progress', label: 'In-Progress' },
    { id: 'viewed', label: 'Viewed' },
    { id: 'approved', label: 'Approved' },
    { id: 'rejected', label: 'Rejected' },
    { id: 'archive', label: 'Archived' },
  ];

  // Role-based access control
  isManager: boolean = false;
  isSecretary: boolean = false;
  isAdmin: boolean = false;

  // ---- DocuSign Approve/Reject (opener side) ----
  // Keys MUST match routing-slip-details.component.ts so the DocuSign return tab can read them.
  private readonly docuSignPendingStorageKey = 'routingSlipDocuSignPending';
  private readonly docuSignEnvelopeIdStorageKey = 'routingSlipDocuSignEnvelopeId';
  private readonly docuSignPendingCommentStorageKey = 'routingSlipDocuSignApprovalComment';
  private readonly docuSignPendingActionStorageKey = 'routingSlipDocuSignPendingAction';
  private readonly docuSignApprovalSavedBroadcastKey = 'routingSlipDocuSignApprovalSavedBroadcast';
  /** True from "Sign and Submit" until the DocuSign tab opens fails, the signing tab closes, or a terminal event arrives. Drives the page loader. */
  isDocuSignSigningInProgress = false;
  private docuSignWindowPollId: ReturnType<typeof setInterval> | null = null;
  private docuSignOpenerMessageHandler: ((ev: MessageEvent) => void) | null = null;
  private docuSignStorageHandler: ((ev: StorageEvent) => void) | null = null;
  private lastDocusignApprovalSavedHandledAt = 0;
  private lastDocuSignOpenerNotifyKey = '';
  private lastDocuSignOpenerNotifyTime = 0;

  // Sort options
  sortOptions: SortOption[] = [
    { label: 'Newest First', value: 'newest' },
    { label: 'Oldest First', value: 'oldest' },
  ];

  constructor(
    private routingSlipService: RoutingSlipService,
    private docusignService: DocusignService,
    private router: Router,
    private authService: AuthService,
    private messageService: MessageService,
    private documentService: DocumentService
  ) {}

  ngOnInit(): void {
    this.checkUserRole();
    this.setupDocuSignOpenerListener();
    this.initializeTableColumns();
    this.checkMobile();
    this.setupSearchDebounce();

    // Add a small delay to ensure loader is visible
    setTimeout(() => {
      this.loadRoutingSlips();
    }, 100);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearDocuSignWindowPoll();
    if (this.docuSignOpenerMessageHandler) {
      window.removeEventListener('message', this.docuSignOpenerMessageHandler);
      this.docuSignOpenerMessageHandler = null;
    }
    if (this.docuSignStorageHandler) {
      window.removeEventListener('storage', this.docuSignStorageHandler);
      this.docuSignStorageHandler = null;
    }
  }

  private setupSearchDebounce(): void {
    this.searchSubject
      .pipe(
        debounceTime(1000), // 1000ms delay - 1 second for user to finish typing
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((searchTerm: string) => {
        this.searchTerm = searchTerm;
        this.paginationInfo.currentPage = 1;
        this.loadRoutingSlips();
      });
  }

  private checkMobile(): void {
    this.isMobile = window.innerWidth < 768;
  }

  private checkUserRole(): void {
    const currentUser = this.authService.getCurrentUser();

    // Set userProfile from authenticated user
    if (currentUser) {
      this.userProfile = {
        id: currentUser.email,
        name: currentUser.name,
        email: currentUser.email,
        role: currentUser.role,
        avatar: 'assets/images/logo.png',
      };
    }

    this.isManager = this.authService.hasRole(UserRole.MANAGER);
    this.isSecretary = this.authService.hasRole(UserRole.SECRETARY);
    this.isAdmin = this.authService.hasRole(UserRole.ADMIN);
  }

  /** Manager/Secretary run Approve/Reject through DocuSign (matches routing-slip-details). Admin uses direct API. */
  private get useDocuSignForApprovereject(): boolean {
    return !this.isAdmin && (this.isManager || this.isSecretary);
  }

  private initializeTableColumns(): void {
    this.columns = [
      { key: 'date', label: 'Date', sortable: true },
      { key: 'documentName', label: 'Document Name', sortable: true },
      { key: 'assignedDate', label: 'Assigned Date', sortable: true },
      { key: 'dueDate', label: 'Due Date', sortable: true },
      { key: 'approvalStatus', label: 'Status', sortable: true }, // Approval Status column
    ];
  }

  loadRoutingSlips(): void {
    this.loading = true;

    const pageNumber = this.paginationInfo?.currentPage || 1;
    const pageSize = this.paginationInfo?.itemsPerPage || 10;
    const sortBy = this.selectedSortOption;
    const filter = this.selectedFilter;
    const search = this.searchTerm;

    // First, get the routing slips with permissions
    this.routingSlipService.getRoutingSlipsWithPermissions().subscribe({
      next: (permissionsData: any[]) => {
        // Store permissions in the Map
        permissionsData.forEach((item: any) => {
          if (item.id && item.permission && item.permission.permission) {
            this.routingSlipPermissions.set(
              item.id,
              item.permission.permission
            );
          }
        });

        // Now get the paginated list
        this.routingSlipService
          .getAllRoutingSlips(
            pageNumber,
            pageSize,
            sortBy,
            filter,
            search,
            this.selectedFilter === 'archive'
          )
          .subscribe({
            next: (response: any) => {
              // Map API response directly to table data
              this.routingSlips = response.items.map((item: any) => ({
                id: item.id,
                documentName: item.documentName,
                date: formatDateDDMMYYYY(item.createdOn) || 'N/A',
                assignedDate: formatDateDDMMYYYY(item.createdOn) || 'N/A',
                dueDate: formatDateDDMMYYYY(item.dueDate) || 'N/A',
                approvalStatus: this.formatStatusDescription(
                  item.statusDescription
                ),
                approveRejectStatus: item.approveRejectStatus, // Preserve numeric status for action menu
              }));

              // Update pagination info
              this.paginationInfo = {
                currentPage: response.pageNumber,
                totalPages: Math.ceil(response.totalCount / response.pageSize),
                totalItems: response.totalCount,
                itemsPerPage: response.pageSize,
                startItem: (response.pageNumber - 1) * response.pageSize + 1,
                endItem: Math.min(
                  response.pageNumber * response.pageSize,
                  response.totalCount
                ),
              };

              this.loading = false;

              // Ensure loader shows for minimum duration
              setTimeout(() => {
                this.pageLoading = false;
              }, 500);

              // Update filter tabs with counts, using current response for active filter
              this.updateFilterTabs(response.totalCount);
            },
            error: (error) => {
              this.loading = false;
              this.pageLoading = false;
              this.routingSlips = [];
            },
          });
      },
      error: (error) => {
        this.loading = false;
        this.pageLoading = false;
        this.routingSlips = [];
      },
    });
  }

  onFilterTabChange(tabId: string): void {
    this.selectedFilter = tabId;
    this.paginationInfo.currentPage = 1;
    this.loadRoutingSlips();
  }

  onSearchChange(searchTerm: string): void {
    this.searchSubject.next(searchTerm);
  }

  clearSearch(): void {
    this.onSearchChange('');
  }

  onSortChange(sortOption: string): void {
    this.selectedSortOption = sortOption;
    this.loadRoutingSlips();
  }

  onPageChange(page: number): void {
    if (this.paginationInfo) {
      this.paginationInfo.currentPage = page;
    }
    this.loadRoutingSlips();
  }

  /**
   * Get permission for a routing slip row
   * @param row The routing slip row
   * @returns Permission level (0 = no access, 1 = ViewOnly, 2 = ApproveReject)
   */
  getPermissionForRoutingSlipRow(row: any): number {
    const permission = this.getPermissionForRoutingSlip(row.id);
    return permission;
  }

  onRowClick(routingSlip: RoutingSlip): void {
    // Determine archived status based on current filter tab (archive list)
    const isArchived = this.selectedFilter === 'archive';
    this.router.navigate(['/routing-slip-details'], {
      queryParams: {
        id: routingSlip.id,
        archived: isArchived ? 'true' : undefined,
      },
    });
  }

  onComment(routingSlip: RoutingSlip): void {
    this.openCommentModal();
  }

  onApprove(data: any): void {
    if (!data?.row?.id) {
      return;
    }
    // Manager/Secretary: sign via DocuSign first (same as routing-slip-details). Admin: direct API.
    if (this.useDocuSignForApprovereject) {
      this.submitDocuSignRoutingSlipApprovereject(true, data.row.id, data.comment);
      return;
    }
    this.approveRoutingSlip(data.row.id, data.comment);
  }

  onReject(data: any): void {
    if (!data?.row?.id) {
      return;
    }
    this.rejectRoutingSlip(data.row.id, data.comment);
  }

  onEdit(routingSlip: RoutingSlip): void {
    if (!routingSlip.id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Routing slip ID is missing',
      });
      return;
    }

    // Navigate to edit page with the routing slip ID
    this.router.navigate(['/routing-slip-edit'], {
      queryParams: { id: routingSlip.id },
    });
  }

  onAuditLog(routingSlip: RoutingSlip): void {
    if (!routingSlip.id) {
      return;
    }

    // Set loading state and show modal
    this.auditLoading = true;
    this.selectedRow = routingSlip;
    this.auditDialogVisible = true;

    // Load audit logs from API
    this.routingSlipService.getAuditLog(routingSlip.id).subscribe({
      next: (auditLogs) => {
        // Transform the API response to match the expected AuditsData structure
        this.auditData = (auditLogs || []).map((log: any) => ({
          date: this.formatDateForAudit(log.createdOn),
          time: this.formatTimeForAudit(log.createdOn),
          user: log.userName,
          action: log.userAction,
        }));

        this.auditLoading = false;
      },
      error: (error) => {
        this.auditLoading = false;

        // Show error message to user
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load audit logs. Please try again.',
          life: 5000,
        });
      },
    });
  }

  openCommentModal(): void {
    this.modalType = 'comment';
    this.modalTitle = 'Add New Comment:';
    this.showModal = true;
  }

  onModalClose(): void {
    this.showModal = false;
  }

  onModalSubmit(data: any): void {
    this.showModal = false;
  }

  /**
   * Approve a routing slip
   */
  private approveRoutingSlip(id: string, comment: string): void {
    this.routingSlipService
      .approveRejectRoutingSlip(id, true, comment)
      .subscribe({
        next: (response) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Routing slip approved successfully',
          });
          // Refresh the data
          this.loadRoutingSlips();
        },
        error: (error) => {
          const errorMessage = this.extractErrorMessage(error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: errorMessage,
          });
        },
      });
  }

  /**
   * Reject a routing slip
   */
  private rejectRoutingSlip(id: string, comment: string): void {
    this.routingSlipService
      .approveRejectRoutingSlip(id, false, comment)
      .subscribe({
        next: (response) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Routing slip rejected successfully',
          });
          // Refresh the data
          this.loadRoutingSlips();
        },
        error: (error) => {
          const errorMessage = this.extractErrorMessage(error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: errorMessage,
          });
        },
      });
  }

  // ---------------------------------------------------------------------------
  // DocuSign Approve/Reject (opener side)
  //
  // This list is the "opener". When Manager/Secretary approve/reject, we open
  // DocuSign in a new tab. The return URL loads routing-slip-details (module =
  // RoutingSlipDetails), which performs POST .../approvereject and notifies this
  // opener (on success it navigates this tab to the details page with a toast;
  // on failure/cancel it posts a message we handle here to refresh + toast).
  // ---------------------------------------------------------------------------

  /**
   * Open DocuSign in a new tab. The comment and approve/reject intent are stored so the
   * return tab (routing-slip-details) can call POST .../approvereject with `{ isApproved, comment }`.
   */
  private submitDocuSignRoutingSlipApprovereject(
    isApproved: boolean,
    routingSlipId: string,
    comment: string | undefined
  ): void {
    if (!routingSlipId) {
      return;
    }
    // Show the page loader immediately on Sign and Submit; cleared once the tab opens fails or signing ends.
    this.isDocuSignSigningInProgress = true;
    const text = typeof comment === 'string' ? comment : '';
    this.setDocuSignKeyBoth(
      this.docuSignApprovalCommentStorageKeyForSlip(routingSlipId),
      text
    );
    this.setDocuSignKeyBoth(this.docuSignPendingCommentStorageKey, text);
    this.setDocuSignKeyBoth(
      this.docuSignPendingActionStorageKey,
      isApproved ? 'approve' : 'reject'
    );

    this.docusignService
      .addDocusignSignature(routingSlipId, DocusignModule.RoutingSlipDetails)
      .subscribe({
        next: (signing) => {
          const url = signing.signingUrl?.trim();
          if (!url) {
            this.isDocuSignSigningInProgress = false;
            this.clearDocuSignPendingActionKeys(routingSlipId);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail:
                'No signing URL was returned from the server. Please try again.',
            });
            return;
          }
          this.setDocuSignKeyBoth(this.docuSignPendingStorageKey, routingSlipId);
          const envelopeId = signing.envelopeId?.trim();
          if (envelopeId) {
            this.setDocuSignKeyBoth(
              this.docuSignEnvelopeIdStorageKey,
              envelopeId
            );
          } else {
            this.removeDocuSignKeyBoth(this.docuSignEnvelopeIdStorageKey);
          }
          const tab = window.open(url, '_blank');
          if (!tab) {
            this.isDocuSignSigningInProgress = false;
            this.clearDocuSignSessionKeys();
            this.messageService.add({
              severity: 'warn',
              summary: 'Popup blocked',
              detail:
                'Allow pop-ups for this site to open DocuSign in a new tab.',
            });
            return;
          }
          this.startDocuSignWindowClosedPoll(tab, routingSlipId);
          this.messageService.add({
            severity: 'info',
            summary: 'DocuSign',
            detail: 'Complete signing in the new tab. This tab stays open.',
          });
        },
        error: (error) => {
          this.isDocuSignSigningInProgress = false;
          this.clearDocuSignPendingActionKeys(routingSlipId);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail:
              this.extractErrorMessage(error) ||
              'Error signing document. Please try again.',
          });
        },
      });
  }

  /**
   * Same-origin DocuSign handoff from the return tab: `postMessage` plus `storage` fallback when
   * `window.opener` is unset. On success the return tab usually navigates this tab to the details
   * page; these handlers cover the failure/cancel and no-opener cases.
   */
  private setupDocuSignOpenerListener(): void {
    this.docuSignOpenerMessageHandler = (ev: MessageEvent) => {
      if (ev.origin !== window.location.origin) {
        return;
      }
      const d = ev.data;
      if (!d || typeof d !== 'object') {
        return;
      }
      if (
        d.type === 'docusign-approval-saved' ||
        d.type === 'docusign-approvereject-saved' ||
        d.type === 'routing-slip-approvereject-docusign-saved'
      ) {
        const isApproved =
          typeof (d as { isApproved?: boolean }).isApproved === 'boolean'
            ? (d as { isApproved: boolean }).isApproved
            : true;
        this.handleDocusignApproverejectSavedOnOpener(isApproved);
        return;
      }
      if (
        d.type === 'docusign-routing-slip-finished' &&
        d.terminal === 'fail'
      ) {
        this.handleDocuSignTerminalEventFromOpener(
          d.routingSlipId as string,
          d.docusignEvent as string | null
        );
      }
    };
    window.addEventListener('message', this.docuSignOpenerMessageHandler);

    this.docuSignStorageHandler = (ev: StorageEvent) => {
      if (ev.storageArea !== localStorage) {
        return;
      }
      if (ev.key !== this.docuSignApprovalSavedBroadcastKey || !ev.newValue) {
        return;
      }
      let isApprovedSaved: boolean | undefined;
      try {
        const parsed = JSON.parse(ev.newValue) as { isApproved?: boolean };
        isApprovedSaved = parsed.isApproved;
      } catch {
        return;
      }
      this.handleDocusignApproverejectSavedOnOpener(
        typeof isApprovedSaved === 'boolean' ? isApprovedSaved : true
      );
    };
    window.addEventListener('storage', this.docuSignStorageHandler);
  }

  /** Return tab already called POST .../approvereject; refresh the list and toast here. */
  private handleDocusignApproverejectSavedOnOpener(isApproved: boolean): void {
    const t = Date.now();
    if (t - this.lastDocusignApprovalSavedHandledAt < 800) {
      return;
    }
    this.lastDocusignApprovalSavedHandledAt = t;

    this.isDocuSignSigningInProgress = false;
    this.clearDocuSignWindowPoll();
    this.clearDocuSignSessionKeys();
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: isApproved
        ? 'Routing slip approved successfully.'
        : 'Routing slip rejected successfully.',
    });
    this.loadRoutingSlips();
  }

  private handleDocuSignTerminalEventFromOpener(
    routingSlipId: string,
    docusignEvent: string | null
  ): void {
    const notifyKey = `${routingSlipId}:${docusignEvent ?? ''}`;
    const t = Date.now();
    if (
      this.lastDocuSignOpenerNotifyKey === notifyKey &&
      t - this.lastDocuSignOpenerNotifyTime < 500
    ) {
      return;
    }
    this.lastDocuSignOpenerNotifyKey = notifyKey;
    this.lastDocuSignOpenerNotifyTime = t;

    this.isDocuSignSigningInProgress = false;
    this.clearDocuSignSessionKeys();
    this.clearDocuSignWindowPoll();
    this.messageService.add({
      severity: 'info',
      summary: 'DocuSign',
      detail: this.messageForDocuSignTerminalEvent(docusignEvent),
    });
    this.loadRoutingSlips();
  }

  private messageForDocuSignTerminalEvent(docusignEvent: string | null): string {
    const e = (docusignEvent || '').toLowerCase().trim();
    switch (e) {
      case 'cancel':
        return 'Signing was cancelled in DocuSign. You can start again with Approve and Sign and Submit when you are ready.';
      case 'decline':
        return 'The document was declined in DocuSign.';
      case 'session_timeout':
        return 'The DocuSign session timed out. Please try again.';
      case 'ttl_expired':
        return 'The signing link has expired. Please try again.';
      case 'exception':
        return 'An error occurred in DocuSign. You can try signing again from Approve and Sign and Submit.';
      default:
        return 'Signing did not complete in DocuSign. You can try again from Approve and Sign and Submit when you are ready.';
    }
  }

  /**
   * Poll the DocuSign tab. If the user closes it without a successful return, drop the pending state
   * and remove the abandoned envelope so the slip can be signed again later.
   */
  private startDocuSignWindowClosedPoll(
    signingWindow: Window,
    routingSlipId: string
  ): void {
    this.clearDocuSignWindowPoll();
    this.docuSignWindowPollId = setInterval(() => {
      if (!signingWindow.closed) {
        return;
      }
      this.clearDocuSignWindowPoll();
      if (this.getPendingDocuSignRoutingSlipId() === routingSlipId) {
        this.handleDocuSignTabClosedWithoutCompletion();
      }
    }, 1000);
  }

  private clearDocuSignWindowPoll(): void {
    if (this.docuSignWindowPollId !== null) {
      clearInterval(this.docuSignWindowPollId);
      this.docuSignWindowPollId = null;
    }
  }

  private handleDocuSignTabClosedWithoutCompletion(): void {
    const envelopeId = (
      sessionStorage.getItem(this.docuSignEnvelopeIdStorageKey) ||
      localStorage.getItem(this.docuSignEnvelopeIdStorageKey)
    )?.trim();
    this.isDocuSignSigningInProgress = false;
    this.clearDocuSignSessionKeys();

    if (envelopeId) {
      this.docusignService.removeEnvelope(envelopeId).subscribe({
        next: (message) => {
          this.messageService.add({
            severity: 'success',
            summary: 'DocuSign',
            detail: message,
          });
          this.loadRoutingSlips();
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error signing document. Please try again.',
          });
          this.loadRoutingSlips();
        },
      });
      return;
    }

    this.messageService.add({
      severity: 'warn',
      summary: 'DocuSign',
      detail:
        'The signing window was closed before completion. You can use Approve and Sign and Submit again when you are ready.',
    });
    this.loadRoutingSlips();
  }

  // ---- DocuSign storage helpers (mirror routing-slip-details so the return tab reads the same keys) ----

  private setDocuSignKeyBoth(key: string, value: string): void {
    try {
      sessionStorage.setItem(key, value);
      localStorage.setItem(key, value);
    } catch {
      /* storage quota or disabled */
    }
  }

  private removeDocuSignKeyBoth(key: string): void {
    try {
      sessionStorage.removeItem(key);
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  }

  private docuSignApprovalCommentStorageKeyForSlip(slipId: string): string {
    return `${this.docuSignPendingCommentStorageKey}:${slipId}`;
  }

  private getPendingDocuSignRoutingSlipId(): string | null {
    return (
      sessionStorage.getItem(this.docuSignPendingStorageKey) ||
      localStorage.getItem(this.docuSignPendingStorageKey)
    );
  }

  private clearDocuSignPendingActionKeys(routingSlipId: string): void {
    this.removeDocuSignKeyBoth(this.docuSignPendingCommentStorageKey);
    this.removeDocuSignKeyBoth(
      this.docuSignApprovalCommentStorageKeyForSlip(routingSlipId)
    );
    this.removeDocuSignKeyBoth(this.docuSignPendingActionStorageKey);
  }

  private clearDocuSignSessionKeys(): void {
    const pendingId = this.getPendingDocuSignRoutingSlipId();
    this.removeDocuSignKeyBoth(this.docuSignPendingStorageKey);
    this.removeDocuSignKeyBoth(this.docuSignEnvelopeIdStorageKey);
    this.removeDocuSignKeyBoth(this.docuSignPendingCommentStorageKey);
    this.removeDocuSignKeyBoth(this.docuSignPendingActionStorageKey);
    if (pendingId) {
      this.removeDocuSignKeyBoth(
        this.docuSignApprovalCommentStorageKeyForSlip(pendingId)
      );
    }
  }

  /**
   * Format date for audit logs
   */
  private formatDateForAudit(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  }

  /**
   * Format time for audit logs
   */
  private formatTimeForAudit(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  onAuditClose(): void {
    this.auditDialogVisible = false;
    this.auditLoading = false;
  }

  private getArchiveCount(): Observable<number> {
    return this.routingSlipService
      .getAllRoutingSlips(1, 1, 'newest', 'all', undefined, true)
      .pipe(map((response) => response.totalCount));
  }

  private updateFilterTabs(currentFilterCount?: number): void {
    // Get counts for each filter from separate API calls
    const allCount$ = this.routingSlipService
      .getAllRoutingSlips(1, 1, 'newest', 'all')
      .pipe(map((response) => response.totalCount));
    
    const inProgressCount$ = this.routingSlipService
      .getAllRoutingSlips(1, 1, 'newest', 'in-progress')
      .pipe(map((response) => response.totalCount));
    
    const viewedCount$ = this.routingSlipService
      .getAllRoutingSlips(1, 1, 'newest', 'viewed')
      .pipe(map((response) => response.totalCount));
    
    const approvedCount$ = this.routingSlipService
      .getAllRoutingSlips(1, 1, 'newest', 'approved')
      .pipe(map((response) => response.totalCount));
    
    const rejectedCount$ = this.routingSlipService
      .getAllRoutingSlips(1, 1, 'newest', 'rejected')
      .pipe(map((response) => response.totalCount));
    
    const archiveCount$ = this.getArchiveCount();

    // Combine all counts
    combineLatest([
      allCount$,
      inProgressCount$,
      viewedCount$,
      approvedCount$,
      rejectedCount$,
      archiveCount$,
    ]).subscribe(
      ([
        allCount,
        inProgressCount,
        viewedCount,
        approvedCount,
        rejectedCount,
        archiveCount,
      ]) => {
        // Use current filter count if provided (more accurate - comes from actual filtered response)
        let finalInProgressCount = inProgressCount;
        let finalViewedCount = viewedCount;
        let finalApprovedCount = approvedCount;
        let finalRejectedCount = rejectedCount;
        let finalAllCount = allCount;

        if (currentFilterCount !== undefined && currentFilterCount !== null) {
          switch (this.selectedFilter) {
            case 'all':
              finalAllCount = currentFilterCount;
              break;
            case 'in-progress':
              finalInProgressCount = currentFilterCount;
              break;
            case 'viewed':
              finalViewedCount = currentFilterCount;
              break;
            case 'approved':
              finalApprovedCount = currentFilterCount;
              break;
            case 'rejected':
              finalRejectedCount = currentFilterCount;
              break;
          }
        }

        this.filterOptions = [
          { id: 'all', label: 'All', count: finalAllCount },
          { id: 'in-progress', label: 'In-Progress', count: finalInProgressCount },
          { id: 'viewed', label: 'Viewed', count: finalViewedCount },
          { id: 'approved', label: 'Approved', count: finalApprovedCount },
          { id: 'rejected', label: 'Rejected', count: finalRejectedCount },
          { id: 'archive', label: 'Archived', count: archiveCount },
        ];
      }
    );
  }

  // Document viewer methods
  onDocumentNameClick(routingSlip: RoutingSlip): void {
    if (!routingSlip.id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No routing slip ID available for document download.',
      });
      return;
    }

    this.documentLoading = true;
    this.currentDocumentName = routingSlip.documentName || '';
    this.showDocumentViewer = true;
    this.currentDocument = null;
    this.openRoutingSlipDocument(
      routingSlip.id,
      routingSlip.documentName || undefined
    );
  }

  /**
   * Signed: GET /api/Docusign/download/{documentId}.
   * Not signed: GET /api/RoutingSlip/{id}/download.
   */
  private openRoutingSlipDocument(
    documentId: string,
    fallbackFileName?: string
  ): void {
    this.docusignService
      .getDownloadResponse(documentId)
      .pipe(
        switchMap((res) => {
          if (res && res.success === false) {
            return this.routingSlipService.getRoutingSlipDocumentForViewer(
              documentId
            );
          }
          try {
            return of({
              kind: 'docusign' as const,
              data: this.docusignService.parseDocusignFilePayload(res),
            });
          } catch (err) {
            return throwError(() =>
              err instanceof Error ? err : new Error(String(err))
            );
          }
        })
      )
      .subscribe({
        next: (payload) => {
          if ('kind' in payload && payload.kind === 'docusign') {
            const { blob, fileName: apiFileName } = payload.data;
            const fileName =
              apiFileName || fallbackFileName || 'document.pdf';
            const type =
              blob.type && blob.type !== 'application/octet-stream'
                ? blob.type
                : this.getFileTypeFromName(fileName);
            this.setViewerFromBlob(blob, fileName, type || 'application/pdf');
            this.documentLoading = false;
            return;
          }

          if (payload.kind === 'blob') {
            this.setViewerFromBlob(
              payload.blob,
              payload.filename,
              payload.type
            );
            this.documentLoading = false;
            return;
          }

          this.currentDocument = {
            name: payload.filename,
            size: '-',
            type: payload.type,
            url: payload.url,
            lastModified: Date.now(),
          };
          this.documentLoading = false;
        },
        error: () => {
          this.documentLoading = false;
          this.showDocumentViewer = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load document. Please try again.',
          });
        },
      });
  }

  private setViewerFromBlob(blob: Blob, fileName: string, type: string): void {
    const objectUrl = window.URL.createObjectURL(blob);
    this.currentDocument = {
      name: fileName,
      size: this.formatFileSize(blob.size),
      type,
      fileData: objectUrl,
      lastModified: Date.now(),
      blobUrl: objectUrl,
      originalBlob: blob,
    };
  }

  private getFileTypeFromName(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return 'application/pdf';
      case 'doc':
        return 'application/msword';
      case 'docx':
        return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      case 'txt':
        return 'text/plain';
      default:
        return 'application/octet-stream';
    }
  }

  onDocumentViewerClose(): void {
    // Clean up blob URL to prevent memory leaks
    if (this.currentDocument?.blobUrl) {
      window.URL.revokeObjectURL(this.currentDocument.blobUrl);
    }
    this.showDocumentViewer = false;
    this.currentDocument = null;
    this.currentDocumentName = ''; // Clear document name
  }

  onDocumentDownload(attachment: DocumentAttachment): void {
    // If we have the original blob from the API, use it directly
    if (attachment.originalBlob) {
      // Create download link
      const url = window.URL.createObjectURL(attachment.originalBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.name;

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // this.messageService.add({
      //   severity: 'success',
      //   summary: 'Success',
      //   detail: 'Document downloaded successfully.',
      // });
    } else {
      // Fallback to local document service if no cached blob
      this.documentService.downloadDocument(attachment);
    }
  }

  onDocumentOpenInNewTab(attachment: DocumentAttachment): void {
    this.documentService.viewDocument(attachment);
  }

  /**
   * Format file size in human readable format
   * @param bytes File size in bytes
   * @returns Formatted file size string
   */
  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Get permission level for a routing slip
   * @param routingSlipId The routing slip ID
   * @returns Permission level (0 = no access, 1 = ViewOnly, 2 = ApproveReject)
   */
  getPermissionForRoutingSlip(routingSlipId: string): number {
    return this.routingSlipPermissions.get(routingSlipId) || 0;
  }

  /**
   * Format status description text for display (from statusDescription field)
   * Maps statusDescription values: "Pending" -> "In Progress", "In-Progress" -> "In Progress", "Viewed" -> "Viewed"
   * @param statusDescription Status description from API
   * @returns Formatted status text
   */
  private formatStatusDescription(statusDescription?: string): string {
    if (!statusDescription) return 'N/A';
    
    const statusLower = statusDescription.toLowerCase().trim();
    
    // Map "Pending" to "In Progress"
    if (statusLower === 'pending') {
      return 'In Progress';
    }
    
    // Normalize "In-Progress" or "In Progress" to "In Progress"
    if (statusLower === 'in-progress' || statusLower === 'in progress') {
      return 'In Progress';
    }
    
    // Keep "Viewed" as is
    if (statusLower === 'viewed') {
      return 'Viewed';
    }
    
    // Keep "Approved" and "Rejected" as is
    if (statusLower === 'approved') {
      return 'Approved';
    }
    
    if (statusLower === 'rejected') {
      return 'Rejected';
    }
    
    // For other statuses, return as-is (capitalize first letter of each word)
    return statusDescription
      .split(/[\s-]+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  /**
   * Extract error message from HTTP error response
   * @param error HTTP error object
   * @returns Extracted error message or fallback message
   */
  private extractErrorMessage(error: any): string {
    const fallback = 'Something went wrong. Please try again.';
    const body = error?.error;
    // Backend may return a plain string (e.g. "User already worked on this document...")
    if (typeof body === 'string' && body.trim()) {
      return body.trim();
    }
    // ...or a JSON object with the message under a common key.
    if (body && typeof body === 'object') {
      const message = body.message ?? body.error ?? body.detail ?? body.title;
      if (typeof message === 'string' && message.trim()) {
        return message.trim();
      }
    }
    if (typeof error?.message === 'string' && error.message.trim()) {
      return error.message.trim();
    }
    return fallback;
  }

  /**
   * Handle Archive button click - show delete confirmation modal
   * @param routingSlip The routing slip to archive
   */
  onArchive(routingSlip: RoutingSlip): void {
    this.selectedRoutingSlipForDelete = routingSlip;
    this.showDeleteModal = true;
  }

  /**
   * Confirm delete/archive operation
   */
  confirmDelete(): void {
    if (!this.selectedRoutingSlipForDelete?.id) {
      return;
    }

    this.deleteLoading = true;

    this.routingSlipService.deleteRoutingSlip(this.selectedRoutingSlipForDelete.id).subscribe({
      next: (response) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Routing slip archived successfully',
        });
        
        // Close the modal and reset state
        this.showDeleteModal = false;
        this.selectedRoutingSlipForDelete = null;
        this.deleteLoading = false;
        
        // Refresh the data
        this.loadRoutingSlips();
      },
      error: (error) => {
        const errorMessage = this.extractErrorMessage(error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: errorMessage,
        });
        this.deleteLoading = false;
      },
    });
  }

  /**
   * Cancel delete/archive operation
   */
  cancelDelete(): void {
    this.showDeleteModal = false;
    this.selectedRoutingSlipForDelete = null;
  }
}
